import type { MediaDevice } from 'devices/types';

/**
 * Which camera, microphone and speaker to use when the user has not said, decided afresh on every
 * device-list change. Rules, from real MacBook + Logitech BRIO lists in Chrome, Firefox and Safari
 * (2026-10-11):
 *
 * - A device the user picked wins while it is connected.
 * - Cameras never switch by themselves: the one in use is kept while connected. Otherwise an
 *   external camera beats a built-in one, and Desk View (a top-down crop of the desk) comes last.
 * - Microphone and speaker follow the system default, so a headset the OS switches to is followed.
 *   The one exception: with an external camera selected, a built-in default microphone gives way to
 *   the camera's own microphone, or any other external one. A MacBook with its lid closed still
 *   lists its microphone, which then records nothing, and macOS can keep it as the default.
 */

export type PickReason =
  /** The user's own pick, still connected. */
  | 'saved'
  /** The camera already in use. */
  | 'kept'
  | 'external-camera'
  /** The selected external camera's own microphone, over a built-in default. */
  | 'external-camera-mic'
  /** Another external microphone, over a built-in default, while an external camera is selected. */
  | 'external-mic'
  | 'system-default'
  | 'none';

export type DevicePick = { deviceId: string; reason: PickReason };

export type DeviceChoices = Record<'camera' | 'microphone' | 'speaker', DevicePick>;

export type ChooseDevicesInput = {
  cameras: MediaDevice[];
  /** As `mapDevicesForOurApp` stores them: the system default first. */
  microphones: MediaDevice[];
  /** As `mapDevicesForOurApp` stores them: the system default first. */
  speakers: MediaDevice[];
  /** The camera in use now, `''` for none. */
  currentCameraId: string;
  /** The user's own picks, from storage. */
  saved: Partial<Record<'camera' | 'microphone' | 'speaker', string | null>>;
};

/**
 * Chrome marks built-in devices with `(Built-in)` or a `0000:` id ("MacBook Pro Camera (0000:0001)");
 * Firefox and Safari give the bare name. An Apple vendor id is not enough: a Studio Display camera is
 * external. "HD Webcam" is not a built-in name either: Logitech's C270 is called "HD Webcam C270".
 * A phone's cameras ("Front Camera", "camera2 1, facing front") are built-in too.
 */
const BUILT_IN =
  /\(built-in\)|\(0000:[0-9a-f]{4}\)|\b(built-?in|integrated|internal|facetime|macbook|front|back|rear)\b/i;
const DESK_VIEW = /\bdesk view\b/i;

export const isBuiltInDevice = (device: MediaDevice): boolean => BUILT_IN.test(device.label);

const NONE: DevicePick = { deviceId: '', reason: 'none' };
const pick = (device: MediaDevice, reason: PickReason): DevicePick => ({ deviceId: device.deviceId, reason });

/** Windows' `communications` entry duplicates a real device; it is never a choice of its own. */
const usable = (devices: MediaDevice[]) =>
  devices.filter((device) => device.deviceId !== '' && device.deviceId !== 'communications');

const byId = (devices: MediaDevice[], deviceId: string | null | undefined) =>
  deviceId ? devices.find((device) => device.deviceId === deviceId) : undefined;

const chooseCamera = (cameras: MediaDevice[], savedId: string | null | undefined, currentId: string) => {
  const saved = byId(cameras, savedId);
  if (saved) return { device: saved, pick: pick(saved, 'saved') };
  const current = byId(cameras, currentId);
  if (current) return { device: current, pick: pick(current, 'kept') };
  const external = cameras.find((camera) => !isBuiltInDevice(camera) && !DESK_VIEW.test(camera.label));
  if (external) return { device: external, pick: pick(external, 'external-camera') };
  const first = cameras.find((camera) => !DESK_VIEW.test(camera.label)) ?? cameras[0];
  return first ? { device: first, pick: pick(first, 'system-default') } : { device: undefined, pick: NONE };
};

const chooseMicrophone = (
  microphones: MediaDevice[],
  savedId: string | null | undefined,
  camera: MediaDevice | undefined
): DevicePick => {
  const saved = byId(microphones, savedId);
  if (saved) return pick(saved, 'saved');
  const systemDefault = microphones[0];
  if (!systemDefault) return NONE;
  if (!isBuiltInDevice(systemDefault) || !camera || isBuiltInDevice(camera)) {
    return pick(systemDefault, 'system-default');
  }
  // Chrome and Firefox give a webcam's camera and microphone one group; Safari gives them two, but
  // the same name.
  const own =
    microphones.find((mic) => camera.groupId !== '' && mic.groupId === camera.groupId) ??
    microphones.find((mic) => mic.label === camera.label);
  if (own) return pick(own, 'external-camera-mic');
  const external = microphones.find((mic) => !isBuiltInDevice(mic));
  return external ? pick(external, 'external-mic') : pick(systemDefault, 'system-default');
};

const chooseSpeaker = (speakers: MediaDevice[], savedId: string | null | undefined): DevicePick => {
  const saved = byId(speakers, savedId);
  if (saved) return pick(saved, 'saved');
  return speakers[0] ? pick(speakers[0], 'system-default') : NONE;
};

export const chooseDevices = ({
  cameras,
  microphones,
  speakers,
  currentCameraId,
  saved,
}: ChooseDevicesInput): DeviceChoices => {
  const camera = chooseCamera(usable(cameras), saved.camera, currentCameraId);
  return {
    camera: camera.pick,
    microphone: chooseMicrophone(usable(microphones), saved.microphone, camera.device),
    speaker: chooseSpeaker(usable(speakers), saved.speaker),
  };
};
